import { plain } from '../../lib/labels';
import { useEffect, useState, type ReactNode } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  type TableColumnsType,
} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { Column } from '@ant-design/plots';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { adminLinks } from '../../lib/adminLinks';
import { api } from '../../api/client';
import { usePermissions } from '../../auth/store';
import { formatCoins, formatRupees, ledgerReasonLabel, paiseToRupees } from '../../lib/money';
import { CallHistoryCard, type CallRowDetail } from './CallHistoryExpanded';
import { CreditEarningsButton, ReverseCreditButton, formatPaiseExact } from './ListenerEarningsCredit';
import { CallDetailView, type CallDetail } from '../calls/CallDetailView';
import { PayoutHistory } from '../payouts/PayoutHistory';
import { ListenerSuspensionControl } from './ListenerSuspensionControl';
import { UserName, listenerLabel, listenerSecondary } from '../../lib/listenerName';
import { ApplicationTab, ReferralTab, StatusHistoryTab, useListenerProfile } from './ListenerDetailTabs';
import { OUTCOME_FILTERS, type CallOutcomeFilter } from '../calls/callOutcome';
import {
  END_REASON_LABEL,
  LOGIN_METHOD_LABEL,
  formatDuration,
  formatWhen,
  giftLabel,
  type EarningRow,
  type GiftEarningLine,
  type LiveStreamRow,
  type ListenerOverview,
  type LoginSessionRow,
  type OnlineSessionRow,
  type Paged,
  type StatsRangeParams, RANGE_PRESETS } from './listenerStats';
import {
  LiveDuration,
  LoginStatusTag,
  OnlineStatusTag,
  OnlineTodayCard,
  useNow,
  useUrlStatsRange,
} from './ListenerStatsWidgets';
import { EmploymentTab } from './employment/EmploymentTab';
import { ActivityLogsTab } from '../logs/ActivityTimeline';
import type { BadgeMap } from './employment/employmentTypes';

/**
 * The old stand-alone address `/listeners/:id` (bookmarks, old links): listener
 * details now open beside the Listeners list, so send the admin there with the
 * same `?tab=` / range params.
 */
export function ListenerDetailPage() {
  const { id = '' } = useParams();
  const [searchParams] = useSearchParams();
  const target = new URL(adminLinks.listener(id), 'http://x');
  for (const [k, v] of searchParams) if (!target.searchParams.has(k)) target.searchParams.set(k, v);
  return <Navigate to={target.pathname + target.search} replace />;
}

/**
 * One listener's activity and earnings, rendered in the right-hand pane of the
 * Listeners page (lib/MasterDetail). Login time, online time, call time and
 * earnings are shown as SEPARATE sections — each is its own backend metric
 * (login_sessions / listener_online_sessions / call_sessions / the ledger).
 *
 * The date range is the list's range (`?range=`): one picker at the top of the
 * page drives the list figures and this pane alike.
 */
export function ListenerDetailView({
  id,
  onClose,
  extra,
  tabParam = 'tab',
}: {
  id: string;
  onClose: () => void;
  /** Page-level actions for this listener (e.g. Review), shown in the header. */
  extra?: ReactNode;
  /**
   * URL param holding the open tab: `tab` on the Listeners page; `dtab` when
   * the pane is shown beside another page (whose own `?tab=` must stay).
   */
  tabParam?: string;
}) {
  const { can } = usePermissions();
  const { preset, custom, range } = useUrlStatsRange('today');
  // Name the picked range on every "(range)" figure, so "Yesterday" is never
  // mistaken for the always-today figures next to it.
  const rangeLabel =
    preset === 'custom'
      ? custom.from && custom.to
        ? `${custom.from} – ${custom.to}`
        : 'Custom range'
      : (RANGE_PRESETS.find((p) => p.value === preset)?.label ?? 'Selected range');
  const now = useNow();

  const overview = useQuery({
    queryKey: ['admin', 'listener-overview', id, range],
    queryFn: async () =>
      (await api.get<ListenerOverview>(`/admin/listeners/${id}/overview`, { params: range })).data,
    enabled: range !== null,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
  const o = overview.data;
  // Application, KYC and the account behind this listener.
  const profile = useListenerProfile(id);
  // The ONE admin-facing name for this listener, used in the header and every tab.
  const nameSource = { displayName: o?.listener.displayName, handle: o?.listener.handle, id };
  const listenerName = o ? listenerLabel(nameSource) : 'Listener';
  // ?tab= opens a tab directly (cross-links: a payout row → Earnings, KYC → Application…).
  const [searchParams, setSearchParams] = useSearchParams();
  // Employee tag in the header — the cheap badges call, only when the permission is held.
  const canEmployment = can('listeners.employment.view');
  const employmentBadge = useQuery({
    queryKey: ['admin', 'employment', 'badges', id],
    queryFn: async () => (await api.get<BadgeMap>('/admin/employment/badges', { params: { listenerIds: id } })).data,
    enabled: canEmployment && !!id,
    staleTime: 60_000,
  });
  const badge = employmentBadge.data?.[id];

  const tabs = [
    {
      key: 'overview',
      label: 'Overview',
      children: o ? <OverviewTab o={o} now={now} fetchedAt={overview.dataUpdatedAt} rangeLabel={rangeLabel} /> : null,
    },
    {
      key: 'application',
      label: 'Application & KYC',
      children: <ApplicationTab profile={profile.data} loading={profile.isLoading} />,
    },
    ...(can('listeners.activity.view') && range
      ? [
          { key: 'login', label: 'Login history', children: <LoginHistory id={id} range={range} /> },
          {
            key: 'online',
            label: 'Online history',
            children: <OnlineHistory id={id} range={range} series={o?.series ?? null} rangeLabel={rangeLabel} />,
          },
        ]
      : []),
    ...(can('calls.view') && range
      ? [{ key: 'calls', label: 'Call history', children: <CallHistory id={id} range={range} /> }]
      : []),
    ...(can('live.view') && range
      ? [{ key: 'live', label: 'Live history', children: <LiveHistory id={id} range={range} /> }]
      : []),
    ...(can('listeners.earnings.view') && range
      ? [{ key: 'earnings', label: 'Earnings', children: (
            <EarningsHistory
              id={id}
              range={range}
              listenerName={listenerName}
            />
          ) }]
      : []),
    ...(canEmployment
      ? [{ key: 'employment', label: 'Employment', children: <EmploymentTab listenerId={id} listenerName={listenerName} /> }]
      : []),
    ...(can('payouts.view')
      ? [{ key: 'payouts', label: 'Payouts', children: <PayoutHistory listenerId={id} /> }]
      : []),
    ...(can('referral.view')
      ? [{ key: 'referral', label: 'Referral', children: <ReferralTab userId={profile.data?.userId} /> }]
      : []),
    ...(can('audit.view')
      ? [{ key: 'status', label: 'Status history', children: <StatusHistoryTab listenerId={id} /> }]
      : []),
    ...(can('logs.view')
      ? [{ key: 'activity', label: 'Activity logs', children: <ActivityLogsTab listenerId={id} /> }]
      : []),
  ];
  const requestedTab = searchParams.get(tabParam);
  const activeTab = tabs.some((t) => t.key === requestedTab) ? (requestedTab as string) : 'overview';

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <Space align="center" wrap>
          <Typography.Title level={4} style={{ color: 'white', margin: 0 }}>
            {listenerName}
          </Typography.Title>
          {o && listenerSecondary(nameSource) && (
            <Typography.Text type="secondary">{listenerSecondary(nameSource)}</Typography.Text>
          )}
          <Typography.Text type="secondary" copyable={{ text: id }} style={{ fontSize: 12 }}>
            {id.slice(0, 8)}
          </Typography.Text>
          {o && <Tag>{plain(o.listener.status)}</Tag>}
          {o?.listener.isSuspended && <Tag color="error">suspended</Tag>}
          {o?.listener.isDummy && <Tag color="purple">dummy</Tag>}
          {badge && badge.status !== 'ended' && (
            <Tag color="geekblue">Employee · {plain(badge.employmentType)}</Tag>
          )}
          {profile.data?.voiceVerifiedAt && (
            <Tooltip title={`Voice verified ${new Date(profile.data.voiceVerifiedAt).toLocaleString()}`}>
              <Tag color="cyan">voice verified</Tag>
            </Tooltip>
          )}
          {o?.listener.availability && <AvailabilityTags availability={o.listener.availability} />}
          {o?.activity && <LoginStatusTag loggedIn={o.activity.login.loggedIn} />}
          {o?.activity && (
            <OnlineStatusTag
              online={o.activity.online.onlineNow}
              screenOn={o.activity.online.screenOn}
              reachableUntil={o.activity.online.reachableUntil}
            />
          )}
        </Space>
        <Space wrap>
          {extra}
          {o && o.listener.status === 'approved' && (
            <ListenerSuspensionControl
              listenerId={id}
              listenerName={listenerName}
              isSuspended={o.listener.isSuspended}
            />
          )}
          <Button type="text" icon={<CloseOutlined />} aria-label="Close listener details" onClick={onClose} />
        </Space>
      </div>
      {overview.isError && <Alert type="error" showIcon message="Could not load this listener's statistics" />}
      <Card loading={overview.isLoading}>
        <Tabs
          items={tabs}
          activeKey={activeTab}
          onChange={(k) => {
            const next = new URLSearchParams(searchParams);
            if (k === 'overview') next.delete(tabParam);
            else next.set(tabParam, k);
            setSearchParams(next, { replace: true });
          }}
          destroyOnHidden
        />
      </Card>
    </Space>
  );
}

// ---------------------------------------------------------------------------

function Stat({ title, value, hint, span = 6 }: { title: string; value: ReactNode; hint?: string; span?: number }) {
  return (
    <Col xs={12} md={span}>
      <Statistic title={hint ? <Tooltip title={hint}>{title}</Tooltip> : title} valueRender={() => value} />
    </Col>
  );
}

function Section({ title, children, extra }: { title: string; children: ReactNode; extra?: ReactNode }) {
  return (
    <Card size="small" title={title} extra={extra} style={{ marginBottom: 12 }}>
      <Row gutter={[16, 16]}>{children}</Row>
    </Card>
  );
}

function OverviewTab({
  o,
  now,
  fetchedAt,
  rangeLabel,
}: {
  o: ListenerOverview;
  now: number;
  fetchedAt: number;
  /** The date picker's choice, e.g. "Yesterday" — shown on the range figures. */
  rangeLabel: string;
}) {
  const TODAY_HINT = 'Always today (since 12:00 am), whatever range is picked above.';
  const a = o.activity;
  const m = o.money;
  return (
    <>
      {a && (
        <Section title="Login activity" extra={<Typography.Text type="secondary">Authenticated app session — independent of Online</Typography.Text>}>
          <Stat title="Login status" value={<LoginStatusTag loggedIn={a.login.loggedIn} />} />
          <Stat title="Current login duration" value={a.login.loggedIn ? <LiveDuration since={a.login.currentLoginSince} now={now} /> : '—'} />
          <Stat title="Login time today (since 12 am)" value={formatDuration(a.login.todayLoginSeconds)} hint={TODAY_HINT} />
          <Stat title={`Login time · ${rangeLabel}`} value={formatDuration(a.login.loginSeconds)} hint="Overlapping devices counted once" />
          <Stat title={`Logins · ${rangeLabel}`} value={a.login.loginCount} />
          <Stat title="Last login" value={formatWhen(a.login.lastLoginAt)} />
          <Stat title="Last logout" value={formatWhen(a.login.lastLogoutAt)} />
        </Section>
      )}
      {a && (
        <Card
          size="small"
          title="Online today (live)"
          extra={
            <Typography.Text type="secondary">
              Same as the listener&apos;s app card · ticks while online · a locked phone stays online until its reach window ends
            </Typography.Text>
          }
          style={{ marginBottom: 12 }}
        >
          <OnlineTodayCard online={a.online} fetchedAt={fetchedAt} now={now} />
        </Card>
      )}
      {a && (
        <Section title="Online activity" extra={<Typography.Text type="secondary">Available to receive calls</Typography.Text>}>
          <Stat
            title="Online status"
            value={
              <OnlineStatusTag
                online={a.online.onlineNow}
                screenOn={a.online.screenOn}
                reachableUntil={a.online.reachableUntil}
              />
            }
          />
          <Stat title="Current online session" value={<LiveDuration since={a.online.currentOnlineSince} now={now} />} />
          <Stat title="Last went online" value={formatWhen(a.online.lastWentOnlineAt)} />
          <Stat title="Last went offline" value={formatWhen(a.online.lastWentOfflineAt)} />
          <Stat
            title={`Online time · ${rangeLabel}`}
            value={formatDuration(a.online.onlineSeconds)}
            hint="Use this for paying by day: pick the day at the top right. Includes screen-locked time while the listener was reachable."
          />
          <Stat title="Online today (since 12 am)" value={formatDuration(a.online.todayOnlineSeconds)} hint={TODAY_HINT} />
          <Stat
            title="Screen-on today (since 12 am)"
            value={formatDuration(a.online.todayScreenOnSeconds)}
            hint="Online = callable (incl. locked phone within the reach window). Screen-on = app open; only this counts toward online-earning levels."
          />
          <Stat title="This week" value={formatDuration(a.online.weekOnlineSeconds)} hint="Since Monday 00:00" />
          <Stat title="This month" value={formatDuration(a.online.monthOnlineSeconds)} />
          <Stat title="Lifetime online time" value={formatDuration(a.online.lifetimeOnlineSeconds)} hint="All recorded history — not limited to the range" />
        </Section>
      )}
      <Section title={`Calls · ${rangeLabel}`} extra={<Typography.Text type="secondary">By call start time</Typography.Text>}>
        <Stat title="Total calls" value={o.calls.calls} hint="Every call attempt to this listener" />
        <Stat title="Completed (connected)" value={o.calls.connectedCalls} />
        <Stat title="Missed / declined" value={o.calls.missedCalls} />
        <Stat title="Failed to connect" value={o.calls.failedCalls} />
        <Stat title="Call time" value={formatDuration(o.calls.callSeconds)} hint="Connected talk time" />
      </Section>
      {m && (
        <Section title="Earnings" extra={<Typography.Text type="secondary">From the ledger — listeners earn ₹, not coins</Typography.Text>}>
          <Stat title={`Earnings · ${rangeLabel}`} value={formatRupees(m.earningsPaise)} />
          <Stat title="From calls" value={formatRupees(m.callEarningsPaise)} />
          <Stat title="From gifts" value={formatRupees(m.giftEarningsPaise)} />
          <Stat title="From referrals" value={formatRupees(m.referralEarningsPaise)} />
          {m.adminCreditEarningsPaise !== undefined && (
            <Stat
              title="From admin credits"
              value={formatRupees(m.adminCreditEarningsPaise)}
              hint="Manual credits by an admin in the range — not call earnings"
            />
          )}
          <Stat title={`Caller coins · ${rangeLabel}`} value={formatCoins(m.callerCoins)} hint="Coins callers spent on this listener's calls, net of refunds" />
          <Stat title="Lifetime earnings" value={formatRupees(m.lifetimeEarningsPaise)} hint="All time — not limited to the range" />
          <Stat title="Available balance" value={formatRupees(m.availableBalancePaise)} hint="Withdrawable now (earnings − payouts)" />
          <Stat title="Pending payouts" value={formatRupees(m.pendingPayoutPaise)} hint="Requested, not yet paid" />
          <Stat title="Paid out" value={formatRupees(m.paidOutNetPaise)} hint={`Net of TDS · gross ${formatRupees(m.paidOutGrossPaise)}`} />
        </Section>
      )}
      {m && m.giftBreakdown && m.giftBreakdown.length > 0 && (
        <GiftBreakdownCard lines={m.giftBreakdown} totalPaise={m.giftEarningsPaise} />
      )}
      <Charts o={o} />
    </>
  );
}

/** Gift earnings in the range, one row per gift type (ledger credits grouped
 *  by gift; names/emoji from the catalog). Totals to "From gifts". */
export function GiftBreakdownCard({ lines, totalPaise }: { lines: GiftEarningLine[]; totalPaise: string }) {
  const columns: TableColumnsType<GiftEarningLine> = [
    {
      title: 'Gift',
      render: (_: unknown, g) => `${g.emoji ? `${g.emoji} ` : ''}${g.giftName}`,
    },
    { title: 'Received', dataIndex: 'quantity', align: 'right' },
    { title: 'Earnings', align: 'right', render: (_: unknown, g) => formatRupees(g.amountPaise) },
  ];
  return (
    <Card
      size="small"
      title="Gift earnings by type"
      extra={<Typography.Text type="secondary">Range · from the ledger</Typography.Text>}
      style={{ marginBottom: 12 }}
    >
      <Table
        rowKey={(g) => g.giftCode ?? `unknown:${g.giftName}`}
        size="small"
        pagination={false}
        dataSource={lines}
        columns={columns}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0}>
              <Typography.Text strong>Total from gifts</Typography.Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              {lines.reduce((n, g) => n + g.quantity, 0)}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} align="right">
              <Typography.Text strong>{formatRupees(totalPaise)}</Typography.Text>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </Card>
  );
}

function Charts({ o }: { o: ListenerOverview }) {
  if (!o.series) {
    return <Alert type="info" showIcon message="Daily charts are available for ranges up to 92 days." />;
  }
  if (o.series.length < 2) return null;
  const online = o.series.map((d) => ({ day: d.day.slice(5), hours: Math.round(((d.onlineSeconds ?? 0) / 3600) * 10) / 10 }));
  const minutes = o.series.map((d) => ({ day: d.day.slice(5), minutes: Math.round(d.callSeconds / 60) }));
  const earnings = o.series.map((d) => ({ day: d.day.slice(5), rupees: paiseToRupees(d.earningsPaise) }));
  return (
    <Row gutter={[12, 12]}>
      {o.activity && (
        <Col xs={24} lg={8}>
          <Card size="small" title="Online hours per day">
            <Column data={online} xField="day" yField="hours" height={220} theme="classicDark" />
          </Card>
        </Col>
      )}
      <Col xs={24} lg={8}>
        <Card size="small" title="Call minutes per day">
          <Column data={minutes} xField="day" yField="minutes" height={220} theme="classicDark" />
        </Card>
      </Col>
      {o.money && (
        <Col xs={24} lg={8}>
          <Card size="small" title="Earnings per day (₹)">
            <Column data={earnings} xField="day" yField="rupees" height={220} theme="classicDark" />
          </Card>
        </Col>
      )}
    </Row>
  );
}

// ---- histories --------------------------------------------------------------

function usePagedHistory<T>(path: string, id: string, range: StatsRangeParams) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  useEffect(() => setPage(1), [range]);
  const q = useQuery({
    queryKey: ['admin', 'listener-history', path, id, range, page, pageSize],
    queryFn: async () =>
      (await api.get<Paged<T>>(`/admin/listeners/${id}/${path}`, { params: { ...range, page, pageSize } })).data,
    placeholderData: keepPreviousData,
  });
  const pagination = {
    current: page,
    pageSize,
    total: q.data?.total ?? 0,
    showSizeChanger: true,
    pageSizeOptions: [25, 50, 100],
    onChange: (p: number, size: number) => {
      setPage(size !== pageSize ? 1 : p);
      setPageSize(size);
    },
  };
  return { q, pagination };
}

const dayOf = (iso: string) => new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const timeOf = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }) : '—';

function LoginHistory({ id, range }: { id: string; range: StatsRangeParams }) {
  const { q, pagination } = usePagedHistory<LoginSessionRow>('login-sessions', id, range);
  const columns: TableColumnsType<LoginSessionRow> = [
    { title: 'Date', render: (_: unknown, r) => dayOf(r.startedAt) },
    { title: 'Login', render: (_: unknown, r) => timeOf(r.startedAt) },
    { title: 'Logout', render: (_: unknown, r) => (r.status === 'active' ? <Tag color="blue">Still logged in</Tag> : timeOf(r.endedAt)) },
    { title: 'Duration', render: (_: unknown, r) => formatDuration(r.durationSeconds) },
    {
      title: 'Status',
      render: (_: unknown, r) => (
        <Space size={4} wrap>
          <Tag color={r.status === 'active' ? 'blue' : r.status === 'expired' ? 'warning' : undefined}>{plain(r.status)}</Tag>
          {r.endReason && <Typography.Text type="secondary">{END_REASON_LABEL[r.endReason] ?? r.endReason}</Typography.Text>}
        </Space>
      ),
    },
    { title: 'Method', render: (_: unknown, r) => LOGIN_METHOD_LABEL[r.method] ?? r.method },
    { title: 'Last active', render: (_: unknown, r) => formatWhen(r.lastActiveAt) },
  ];
  return <Table rowKey="id" loading={q.isLoading} dataSource={q.data?.items ?? []} columns={columns} pagination={pagination} scroll={{ x: 'max-content' }} />;
}

const LIVE_STATUS_COLOUR: Record<LiveStreamRow['status'], string | undefined> = {
  live: 'red',
  paused: 'orange',
  ended: undefined,
  ended_flagged: 'volcano',
  scheduled: 'blue',
};
const LIVE_STATUS_LABEL: Record<LiveStreamRow['status'], string> = {
  live: 'Live now',
  paused: 'Paused',
  ended: 'Ended',
  ended_flagged: 'Terminated',
  scheduled: 'Scheduled',
};

/**
 * Live streams this listener hosted in the range, newest first. Viewer figures
 * are the stream's own counters (peak and total), not the live headcount.
 */
export function LiveHistory({ id, range }: { id: string; range: StatsRangeParams }) {
  const { q, pagination } = usePagedHistory<LiveStreamRow>('live-streams', id, range);
  const columns: TableColumnsType<LiveStreamRow> = [
    { title: 'Date', render: (_: unknown, r) => dayOf(r.startedAt) },
    { title: 'Started', render: (_: unknown, r) => timeOf(r.startedAt) },
    {
      title: 'Ended',
      render: (_: unknown, r) =>
        r.status === 'live' || r.status === 'paused' ? <Tag color="red">Still live</Tag> : timeOf(r.endedAt),
    },
    { title: 'Duration', render: (_: unknown, r) => formatDuration(r.durationSeconds) },
    {
      title: 'Stream',
      render: (_: unknown, r) => (
        <Space direction="vertical" size={0}>
          <span>{r.title || 'Untitled'}</span>
          {r.category && <Typography.Text type="secondary" style={{ fontSize: 11 }}>{r.category}</Typography.Text>}
        </Space>
      ),
    },
    { title: 'Peak viewers', dataIndex: 'peakViewerCount', align: 'right' },
    { title: 'Total viewers', dataIndex: 'totalViewers', align: 'right' },
    {
      title: 'Status',
      render: (_: unknown, r) => (
        <Space size={4} wrap>
          <Tag color={LIVE_STATUS_COLOUR[r.status]}>{LIVE_STATUS_LABEL[r.status] ?? r.status}</Tag>
          {r.endedReason && <Typography.Text type="secondary">{r.endedReason}</Typography.Text>}
        </Space>
      ),
    },
  ];
  return (
    <Table
      rowKey="id"
      loading={q.isLoading}
      dataSource={q.data?.items ?? []}
      columns={columns}
      pagination={pagination}
      scroll={{ x: 'max-content' }}
      locale={{ emptyText: 'No live streams in this range' }}
    />
  );
}

/**
 * Online time per day for the picked range — what to pay on. Comes from the
 * overview's daily series, where a session that crosses midnight is split
 * between the two days (10:42 pm – 12:30 am = 1h 18m on day 1 + 30m on day 2).
 */
export function OnlineByDay({ series, rangeLabel }: { series: ListenerOverview['series']; rangeLabel: string }) {
  if (!series) {
    return (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Pick a range of 92 days or less to see online time per day."
      />
    );
  }
  const rows = [...series].reverse();
  const totalOnline = series.reduce((s, d) => s + (d.onlineSeconds ?? 0), 0);
  const hasScreen = series.some((d) => d.screenOnSeconds != null);
  const totalScreen = series.reduce((s, d) => s + (d.screenOnSeconds ?? 0), 0);
  const columns: TableColumnsType<(typeof rows)[number]> = [
    { title: 'Day', dataIndex: 'day', render: (d: string) => dayOf(`${d}T12:00:00`) },
    {
      title: (
        <Tooltip title="When users could call this listener, including a locked phone inside the reach window. Use this for paying by day.">
          Online time
        </Tooltip>
      ),
      render: (_: unknown, d) => <b>{formatDuration(d.onlineSeconds ?? 0)}</b>,
    },
    ...(hasScreen
      ? [
          {
            title: (
              <Tooltip title="App open on screen. Only this counts toward the automatic online-earning levels.">
                Screen-on time
              </Tooltip>
            ),
            render: (_: unknown, d: (typeof rows)[number]) => formatDuration(d.screenOnSeconds ?? 0),
          },
        ]
      : []),
  ];
  return (
    <Card size="small" title={`Online time per day · ${rangeLabel}`} style={{ marginBottom: 16 }} data-testid="online-by-day">
      <Table
        rowKey="day"
        size="small"
        pagination={false}
        dataSource={rows}
        columns={columns}
        scroll={{ x: 'max-content', y: 360 }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <b>Total</b>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <b data-testid="online-by-day-total">{formatDuration(totalOnline)}</b>
              </Table.Summary.Cell>
              {hasScreen && <Table.Summary.Cell index={2}>{formatDuration(totalScreen)}</Table.Summary.Cell>}
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </Card>
  );
}

function OnlineHistory({
  id,
  range,
  series,
  rangeLabel,
}: {
  id: string;
  range: StatsRangeParams;
  series: ListenerOverview['series'];
  rangeLabel: string;
}) {
  const { q, pagination } = usePagedHistory<OnlineSessionRow>('online-sessions', id, range);
  const columns: TableColumnsType<OnlineSessionRow> = [
    { title: 'Date', render: (_: unknown, r) => dayOf(r.startedAt) },
    { title: 'Went online', render: (_: unknown, r) => timeOf(r.startedAt) },
    { title: 'Went offline', render: (_: unknown, r) => (r.status === 'active' ? <Tag color="success">Online now</Tag> : timeOf(r.endedAt)) },
    { title: 'Duration', render: (_: unknown, r) => formatDuration(r.durationSeconds) },
    {
      title: 'Status',
      render: (_: unknown, r) => (
        <Space size={4} wrap>
          <Tag color={r.status === 'active' ? 'success' : r.status === 'closing' ? 'warning' : undefined}>{plain(r.status)}</Tag>
          {r.endReason && <Typography.Text type="secondary">{END_REASON_LABEL[r.endReason] ?? r.endReason}</Typography.Text>}
        </Space>
      ),
    },
  ];
  return (
    <>
      <Typography.Paragraph type="secondary">
        Online history = when users could call this listener (includes a locked phone inside the reach window).
      </Typography.Paragraph>
      <OnlineByDay series={series} rangeLabel={rangeLabel} />
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        Sessions
      </Typography.Title>
      <Table rowKey="id" loading={q.isLoading} dataSource={q.data?.items ?? []} columns={columns} pagination={pagination} scroll={{ x: 'max-content' }} />
    </>
  );
}

export function EarningsHistory({
  id,
  range,
  listenerName,
}: {
  id: string;
  range: StatsRangeParams;
  listenerName: string;
}) {
  const { q, pagination } = usePagedHistory<EarningRow>('earnings', id, range);
  const columns: TableColumnsType<EarningRow> = [
    { title: 'Date', render: (_: unknown, r) => formatWhen(r.creditedAt) },
    {
      title: 'Source',
      render: (_: unknown, r) =>
        r.gift ? (
          <Space size={4} wrap>
            <Tag color="magenta">GIFT</Tag>
            <span>{giftLabel(r.gift)}</span>
          </Space>
        ) : r.reason === 'call_earning' ? (
          <Tag color="blue">CALL</Tag>
        ) : r.reason === 'admin_credit' ? (
          <Tag color="gold">ADMIN CREDIT</Tag>
        ) : r.reason === 'admin_credit_reversal' ? (
          <Tag color="red">REVERSAL</Tag>
        ) : (
          ledgerReasonLabel(r.reason, 'credit')
        ),
    },
    {
      title: 'Reason',
      render: (_: unknown, r) => {
        const by = r.adminCredit ?? r.adminReversal;
        return by ? (
          <Typography.Text style={{ maxWidth: 280, display: 'inline-block', whiteSpace: 'pre-wrap' }}>
            {by.reason || '—'}
          </Typography.Text>
        ) : (
          '—'
        );
      },
    },
    {
      title: 'Created by',
      render: (_: unknown, r) => {
        const by = r.adminCredit ?? r.adminReversal;
        return by ? (
          <Tooltip title={by.adminEmail ?? by.adminId ?? ''}>
            <span>{by.adminName ?? 'Admin'}</span>
          </Tooltip>
        ) : (
          '—'
        );
      },
    },
    {
      title: 'Call',
      render: (_: unknown, r) =>
        r.call ? (
          <Space size={4} wrap>
            <Tag>{plain(r.call.mode)}</Tag>
            <Tag>{plain(r.call.callType)}</Tag>
            {r.call.isVerificationCall && <Tag color="purple">Verification</Tag>}
            {r.call.isDummyAttempt && <Tag color="purple">dummy attempt</Tag>}
            {(r.call.callerHandle || r.call.callerDisplayName) && (
              <UserName handle={r.call.callerHandle} displayName={r.call.callerDisplayName} />
            )}
          </Space>
        ) : r.reason === 'call_earning' ? (
          <Typography.Text type="secondary">Call record not found</Typography.Text>
        ) : (
          '—'
        ),
    },
    { title: 'Call duration', render: (_: unknown, r) => (r.call ? formatDuration(r.call.durationSeconds) : '—') },
    { title: 'Caller coins', render: (_: unknown, r) => (r.call ? formatCoins(r.call.callerCoins) : '—') },
    {
      title: 'Earnings',
      render: (_: unknown, r) =>
        r.reason === 'admin_credit_reversal' ? (
          <Typography.Text type="danger">{formatPaiseExact(r.earningsPaise)}</Typography.Text>
        ) : r.reason === 'admin_credit' ? (
          <Space direction="vertical" size={0}>
            <span>{formatPaiseExact(r.earningsPaise)}</span>
            {r.adminCredit?.reversedPaise && r.adminCredit.reversedPaise !== '0' && (
              <Typography.Text type="danger" style={{ fontSize: 11 }}>
                {formatPaiseExact(r.adminCredit.reversedPaise)} reversed
              </Typography.Text>
            )}
          </Space>
        ) : (
          formatRupees(r.earningsPaise)
        ),
    },
    {
      title: 'Transaction',
      render: (_: unknown, r) => (
        <Tooltip title={`${r.ledgerEntries} ledger entr${r.ledgerEntries === 1 ? 'y' : 'ies'} · ${r.referenceType}`}>
          <Typography.Text copyable={{ text: r.referenceId }} style={{ fontSize: 12 }}>
            {r.referenceId.slice(0, 8)}…
          </Typography.Text>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      render: (_: unknown, r) => <Tag color={r.status === 'reversal' ? 'error' : 'success'}>{plain(r.status)}</Tag>,
    },
    {
      title: '',
      // Only an admin credit with something left on it can be reversed.
      render: (_: unknown, r) =>
        r.reason === 'admin_credit' && r.adminCredit?.remainingPaise && r.adminCredit.remainingPaise !== '0' ? (
          <ReverseCreditButton
            listenerId={id}
            listenerName={listenerName}
            transactionId={r.referenceId}
            creditedPaise={r.earningsPaise}
            remainingPaise={r.adminCredit.remainingPaise}
          />
        ) : null,
    },
  ];
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <CreditEarningsButton listenerId={id} listenerName={listenerName} />
      </div>
      <Table
        rowKey={(r) => `${r.reason}:${r.referenceId}:${r.gift?.code ?? ''}`}
        loading={q.isLoading}
        dataSource={q.data?.items ?? []}
        columns={columns}
        pagination={pagination}
        scroll={{ x: 'max-content' }}
      />
    </Space>
  );
}

interface CallRow extends CallRowDetail {
  id: string;
  userHandle?: string;
  mode: 'audio' | 'video';
  isRandom?: boolean;
  status: string;
  startedAt: string | null;
  connectedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  totalChargedCoins?: number | null;
  listenerSharePaise?: string;
}

/**
 * Every call ATTEMPT involving this listener — connected or not (declined,
 * cancelled, no answer, accepted-but-never-connected, setup failed) — from the
 * existing `/admin/calls` list filtered by listener, newest first, paged on the
 * server. The outcome / audio-video filters and their counts are applied by the
 * API, so a filtered page is still complete. Click a call to expand it; "View
 * full timeline" opens the same Call details view the Calls page uses.
 */
export function CallHistory({ id, range }: { id: string; range: StatsRangeParams }) {
  const [outcome, setOutcome] = useState<CallOutcomeFilter | 'all'>('all');
  const [mode, setMode] = useState<'all' | 'audio' | 'video'>('all');
  const [timelineId, setTimelineId] = useState<string | null>(null);
  const q = useInfiniteQuery({
    queryKey: ['admin', 'listener-calls', id, range, outcome, mode],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      (
        await api.get<{
          items: CallRow[];
          nextCursor: string | null;
          outcomeCounts?: Partial<Record<CallOutcomeFilter | 'all', number>>;
        }>('/admin/calls', {
          params: {
            listenerId: id,
            since: range.from,
            until: range.to,
            cursor: pageParam,
            limit: 50,
            outcome: outcome === 'all' ? undefined : outcome,
            mode: mode === 'all' ? undefined : mode,
            // Counts only with the first page — they describe the whole filter.
            includeOutcomeCounts: pageParam ? undefined : true,
          },
        })
      ).data,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const detail = useQuery({
    queryKey: ['admin', 'call', timelineId],
    enabled: !!timelineId,
    queryFn: async () => (await api.get<CallDetail>(`/admin/calls/${timelineId}`)).data,
  });
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const counts = q.data?.pages[0]?.outcomeCounts;
  const withCount = (label: string, v: CallOutcomeFilter | 'all') =>
    counts && counts[v] !== undefined ? `${label} (${counts[v]})` : label;
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Space wrap>
        <Select
          aria-label="Outcome filter"
          data-testid="call-outcome-filter"
          style={{ minWidth: 250 }}
          value={outcome}
          onChange={setOutcome}
          options={OUTCOME_FILTERS.map((f) => ({ value: f.value, label: withCount(f.label, f.value) }))}
        />
        <Select
          aria-label="Call type filter"
          style={{ minWidth: 130 }}
          value={mode}
          onChange={setMode}
          options={[
            { value: 'all', label: 'Audio + video' },
            { value: 'audio', label: 'Audio' },
            { value: 'video', label: 'Video' },
          ]}
        />
      </Space>
      {/* The listener's own Call history, as the app draws it: one card per
          call, click to expand its details + gifts. The data came with the
          list page — expanding never fetches. */}
      {q.isLoading ? (
        <Spin />
      ) : rows.length === 0 ? (
        <Empty description="No calls in this range" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 720 }}>
          {rows.map((r) => (
            <CallHistoryCard key={r.id} row={r} onOpenTimeline={setTimelineId} />
          ))}
        </div>
      )}
      {q.hasNextPage && (
        <Button onClick={() => void q.fetchNextPage()} loading={q.isFetchingNextPage}>
          Load more
        </Button>
      )}
      <Drawer
        width={900}
        open={!!timelineId}
        onClose={() => setTimelineId(null)}
        title={timelineId ? `Call ${timelineId.slice(0, 8)}` : 'Call'}
      >
        {detail.data ? (
          <CallDetailView call={detail.data} />
        ) : detail.isError ? (
          <Alert type="error" showIcon message="Could not load this call" />
        ) : (
          <Spin />
        )}
      </Drawer>
    </Space>
  );
}

/** The listener's Home switches: which of audio / video / chat users can start. */
export function AvailabilityTags({ availability }: { availability: { audio: boolean; video: boolean; chat: boolean } }) {
  const items: Array<[string, boolean]> = [
    ['Audio', availability.audio],
    ['Video', availability.video],
    ['Chat', availability.chat],
  ];
  return (
    <Space size={4} data-testid="listener-availability">
      {items.map(([label, on]) => (
        <Tag key={label} color={on ? 'green' : 'default'}>
          {label} {on ? 'on' : 'off'}
        </Tag>
      ))}
    </Space>
  );
}
